import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { errMsg, FIELD_LABELS, useApi, useTaxonomy } from "../../lib/api";
import { Field, ProtectedTag } from "../../components/kit";

export const DRAFT_KEY = "verge-job-draft";
const KEY = DRAFT_KEY;
const emptyPrivate = () => ({ full_name: "", phone: "", email: "", address: "", notes: "" });
const STEPS = ["Trade & area", "Job details", "Contact & review"];
const blank = () => ({ category_id: "", area_id: "", title: "", summary: "", private: emptyPrivate(),
  client_token: (window.crypto as any)?.randomUUID?.() || `${Date.now()}-${Math.random()}` });

function loadDraft(params: URLSearchParams) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (saved) return { ...blank(), ...saved, private: emptyPrivate() };
  } catch {}
  return { ...blank(), category_id: params.get("category") || "", title: (params.get("q") || "").slice(0, 120) };
}

const valid = (d: any, step: number) =>
  step === 1 ? !!(d.category_id && d.area_id) : step === 2 ? d.title.trim().length >= 3 : !!(d.private.full_name.trim() && (d.private.phone.trim() || d.private.email.trim()));

function Stepper({ step }: { step: number }) {
  return (
    <ol className="mb-6 grid grid-cols-3 gap-2" aria-label="Progress">
      {STEPS.map((s, i) => (
        <li key={s} aria-current={step === i + 1 ? "step" : undefined} className={`rounded-md border-b-4 px-2 py-2 text-xs font-semibold sm:text-sm ${step > i ? "border-[var(--blue)] text-[var(--navy)]" : "border-[var(--steel)] text-[var(--muted)]"}`} data-testid={`wizard-step-indicator-${i + 1}`}>
          {i + 1}. {s}
        </li>
      ))}
    </ol>
  );
}

export default function JobWizard() {
  const api = useApi();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { categories, areas, catName, areaName } = useTaxonomy();
  const [d, setD] = useState<any>(() => loadDraft(params));
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const step = Math.min(3, Math.max(1, Number(params.get("step")) || 1));
  useEffect(() => {
    const { private: _omit, ...publicPart } = d;
    sessionStorage.setItem(KEY, JSON.stringify(publicPart));
  }, [d]);
  const headRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headRef.current?.focus();
  }, [step]);
  if (step > 1 && !valid(d, 1)) return <Navigate to="/customer/new?step=1" replace />;
  if (step > 2 && !valid(d, 2)) return <Navigate to="/customer/new?step=2" replace />;
  const set = (k: string, v: string) => setD((s: any) => ({ ...s, [k]: v }));
  const setP = (k: string, v: string) => setD((s: any) => ({ ...s, private: { ...s.private, [k]: v } }));
  const go = (n: number) => setParams({ step: String(n) });
  const next = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid(d, step)) { toast.error(step === 3 ? "Add your name and a phone or email" : "Please complete the required fields"); return; }
    if (step < 3) { go(step + 1); return; }
    submit();
  };
  const submit = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    try {
      const { client_token, ...rest } = d;
      const req = await api("post", "/customer/requests", { ...rest, client_token });
      sessionStorage.removeItem(KEY);
      toast.success("Job posted. Here are your matches.");
      nav(`/customer/requests/${req.id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      sending.current = false;
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 ref={headRef} tabIndex={-1} className="mb-4 text-3xl font-extrabold text-[var(--navy)] outline-none">Post a job <span className="sr-only">— step {step} of 3: {STEPS[step - 1]}</span></h1>
      <Stepper step={step} />
      <form onSubmit={next} className="card space-y-4" data-testid={`wizard-step-${step}`} noValidate>
        {step === 1 && (
          <>
            <Field label="Trade" id="wz-cat"><select id="wz-cat" className="input" value={d.category_id} onChange={(e) => set("category_id", e.target.value)} data-testid="wizard-category-select">
              <option value="">Choose a trade</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
            <Field label="Service area" id="wz-area"><select id="wz-area" className="input" value={d.area_id} onChange={(e) => set("area_id", e.target.value)} data-testid="wizard-area-select">
              <option value="">Choose an area</option>{areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Field>
          </>
        )}
        {step === 2 && (
          <>
            <Field label="Short title" id="wz-title" hint="Visible to vendors you later share with. No personal details."><input id="wz-title" className="input" maxLength={120} value={d.title} onChange={(e) => set("title", e.target.value)} data-testid="wizard-title-input" /></Field>
            <Field label="Describe the job (non-private)" id="wz-sum"><textarea id="wz-sum" rows={4} maxLength={1000} className="input" value={d.summary} onChange={(e) => set("summary", e.target.value)} data-testid="wizard-summary-input" /></Field>
          </>
        )}
        {step === 3 && (
          <>
            <div className="protected-zone space-y-3">
              <ProtectedTag />
              <p className="text-xs text-[var(--red)]">Hidden from every vendor until you engage one and approve exactly which fields to share. Not saved on this device; re-enter if you leave this page.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {["full_name", "phone", "email", "address"].map((f) => (
                  <Field key={f} label={`${FIELD_LABELS[f]}${f === "full_name" ? " *" : ""}`} id={`wz-${f}`}><input id={`wz-${f}`} className="input" value={d.private[f]} onChange={(e) => setP(f, e.target.value)} data-testid={`wizard-private-${f}-input`} /></Field>
                ))}
              </div>
              <Field label={FIELD_LABELS.notes} id="wz-notes"><textarea id="wz-notes" rows={2} className="input" value={d.private.notes} onChange={(e) => setP("notes", e.target.value)} data-testid="wizard-private-notes-input" /></Field>
            </div>
            <dl className="rounded-md bg-[var(--paper)] p-3 text-sm" data-testid="wizard-review">
              <div><dt className="inline font-semibold">Trade: </dt><dd className="inline">{catName(d.category_id)}</dd></div>
              <div><dt className="inline font-semibold">Area: </dt><dd className="inline">{areaName(d.area_id)}</dd></div>
              <div><dt className="inline font-semibold">Title: </dt><dd className="inline">{d.title}</dd></div>
            </dl>
          </>
        )}
        <div className="flex gap-2">
          {step > 1 && <button type="button" className="btn btn-ghost" onClick={() => go(step - 1)} data-testid="wizard-back-button">Back</button>}
          <button className="btn btn-primary flex-1" disabled={busy} data-testid="wizard-next-button">{step < 3 ? "Continue" : busy ? "Posting…" : "Post job & see matches"}</button>
        </div>
      </form>
    </div>
  );
}
